import { useEffect, useState } from 'react';
import { sessionInfo } from '../people/membersStore.js';
import { Badge, Button, Icon, Modal } from '../ds/index.js';
import {
  notesFor, isNoteNew, markNotesSeen, formatNoteDate,
  type ReleaseItem, type ReleaseNote,
} from '../../lib/releaseNotes.js';

/** Notes affichées d'entrée ; les plus anciennes derrière « Voir les mises à jour précédentes ». */
const SHOWN_AT_FIRST = 1;

/**
 * « Nouveautés » : ce qui a changé à chaque mise à jour (lib/releaseNotes.ts).
 * S'ouvre seule une fois après une mise à jour (GameHUD), et depuis le menu de
 * l'avatar. Un membre ne voit que ce qui le concerne, un admin voit en plus la
 * partie « Pour les admins ». Fermer la fenêtre marque tout comme vu.
 */
export function ReleaseNotesModal({ onClose }: { onClose: () => void }) {
  const { userId, role } = sessionInfo();
  // Lu une fois à l'ouverture : les pastilles « Nouveau » restent pendant la lecture.
  const [notes] = useState(() => notesFor(role).map((n) => ({ note: n, fresh: isNoteNew(userId, n) })));
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { markNotesSeen(userId); }, [userId]);

  const visible = showAll ? notes : notes.slice(0, Math.max(SHOWN_AT_FIRST, notes.filter((n) => n.fresh).length));

  return (
    <Modal title="Nouveautés" onClose={onClose} footer={<Button variant="primary" onClick={onClose}>C’est noté</Button>}>
      {notes.length === 0 && <p className="pl-help">Rien de neuf pour l’instant.</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        {visible.map(({ note, fresh }) => <NoteBlock key={note.id} note={note} fresh={fresh} />)}
      </div>
      {!showAll && notes.length > visible.length && (
        <Button variant="ghost" size="sm" onClick={() => setShowAll(true)} style={{ marginTop: 'var(--space-4)' }}>
          Voir les mises à jour précédentes
        </Button>
      )}
    </Modal>
  );
}

function NoteBlock({ note, fresh }: { note: ReleaseNote; fresh: boolean }) {
  const forAll = note.items.filter((i) => !i.admin);
  const forAdmins = note.items.filter((i) => i.admin);
  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, font: '700 17px/1.3 var(--font-display)' }}>{note.title}</h3>
        {fresh && <Badge tone="primary">Nouveau</Badge>}
      </div>
      <div style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-secondary)', marginTop: 2 }}>
        Mise à jour du {formatNoteDate(note.date)}
      </div>
      <ItemGroup label="Nouveautés" items={forAll.filter((i) => i.kind === 'new')} />
      <ItemGroup label="Corrections" items={forAll.filter((i) => i.kind === 'fix')} />
      <ItemGroup label="Pour les admins" items={forAdmins} />
    </section>
  );
}

function ItemGroup({ label, items }: { label: string; items: ReleaseItem[] }) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: 'var(--space-4)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--color-text-secondary)', marginBottom: 8 }}>{label}</div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((i) => (
          <li key={i.title} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Icon name={i.kind === 'fix' ? 'check' : 'plus'} size={16} className="pl-menu-icon" />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700 }}>{i.title}</div>
              <div style={{ fontSize: 'var(--text-sm)', lineHeight: 1.45, color: 'var(--color-text-secondary)' }}>{i.text}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
